/**
 * Cloud Data Archive contract (appliance uploader ⇄ apps/cloud ⇄ portal).
 *
 * The appliance (apps/api cloudArchiveService) uploads its historian's tag
 * history to the cloud one UTC hour at a time. The cloud keeps each hour as a
 * compressed object in a storage driver (a local directory or any
 * S3-compatible bucket) and indexes it in SQLite or MySQL (ARCHIVE_DB): a chunk catalogue, a tag
 * list and hourly rollups for numeric tags. Raw points are never rows.
 *
 * ── Chunk format (version 1) ───────────────────────────────────────────────
 *
 * One chunk = one appliance × one UTC hour (× one part, see below). The body is
 * gzip of UTF-8 JSON shaped as `ArchiveChunk`:
 *
 *   {
 *     "v": 1,
 *     "hardwareId": "e27aa9b2069e76b41c658727eb1c4ff7",
 *     "hourStart": "2026-09-14T10:00:00.000Z",
 *     "part": 0, "parts": 1,
 *     "devices": { "3": "Press 3" },
 *     "tags": {
 *       "3:Temperature": { "deviceId": "3", "tagPath": "Temperature", "name": "Oil temp",
 *                          "dataType": "float", "points": [[1250, 71.5], [61250, 71.9]] },
 *       "3:Running":     { "deviceId": "3", "tagPath": "Running", "dataType": "bool",
 *                          "points": [[0, true]] },
 *       "3:Recipe":      { "deviceId": "3", "tagPath": "Recipe", "dataType": "string",
 *                          "points": [[900000, "A-12"]] }
 *     }
 *   }
 *
 * - `tagKey` is `<deviceId>:<tagPath>` — the identity the appliance historian
 *   uses (InfluxDB measurement `tagChange`, tags deviceId + tagPath). If one
 *   hour holds the same tag under more than one dataType (its type was changed
 *   mid-hour), every one of those series is keyed `<deviceId>:<tagPath>@<dataType>`
 *   so a restore writes each point back into exactly the series it came from.
 * - `devices` and each tag's `name` are human names captured at upload time,
 *   so an export stays readable after the appliance's configuration changes.
 * - A point is `[msOffsetFromHourStart, value]` or `[msOffset, value, text]`.
 *   `msOffset` is an integer in [0, 3 600 000). `value` is a number, boolean or
 *   string as the historian stored it. `text` is the historian's original
 *   string form when it differs from `String(value)` (e.g. "12.50" for 12.5),
 *   or `null` when the historian stored no string form at all; restores use it
 *   to write back byte-identical points.
 * - Points within a tag are ordered by offset.
 *
 * Parts: an hour too large for one upload (see ARCHIVE_PART_MAX_POINTS) is split
 * into `parts` chunks by TAG — every tag lives in exactly one part, so hourly
 * rollups stay per tag per hour. `part` is 0-based. Single-part chunks may omit
 * both fields (they default to 0 and 1).
 *
 * Upload headers (POST /api/v1/archive/chunks, Content-Type application/gzip):
 * ARCHIVE_HEADERS.hourStart, .pointCount (total points in the chunk),
 * .sha256 (hex sha256 of the gzip bytes as sent), .part and .parts.
 *
 * Stored object key: `archive/<hardwareId>/<yyyy>/<mm>/<dd>/<hh>[.p<part>of<parts>].<sha16>.json.gz`
 * (see archiveObjectKey). The sha suffix makes objects immutable: replacing an
 * hour writes a new object, commits the catalogue, then deletes the old one, so
 * a crash can orphan an object but never leave the catalogue pointing at the
 * wrong bytes.
 *
 * ── Routes ─────────────────────────────────────────────────────────────────
 *
 * Appliance (Authorization: Bearer <licence key>):
 *   POST /api/v1/archive/chunks                    upload one chunk → ArchiveIngestResponse
 *   GET  /api/v1/archive/state                     → ArchiveStateResponse
 *   GET  /api/v1/archive/restores/next             → ArchiveRestoreNextResponse
 *   GET  /api/v1/archive/chunks/:hourStart?part=N  the stored gzip (same headers as the upload)
 *   POST /api/v1/archive/restores/:id/progress     ArchiveRestoreProgressRequest → ArchiveRestoreProgressResponse
 *
 * Portal (customer session cookie):
 *   GET  /api/portal/archive                              → ArchiveOverview
 *   GET  /api/portal/archive/appliances/:id               → ArchiveApplianceSummary
 *   GET  /api/portal/archive/appliances/:id/tags          → ArchiveTagList
 *   GET  /api/portal/archive/appliances/:id/export        → text/csv (query) — POST takes ArchiveExportRequest
 *   GET  /api/portal/archive/appliances/:id/restores      → ArchiveRestore[]
 *   POST /api/portal/archive/appliances/:id/restores      CreateArchiveRestoreRequest → ArchiveRestore (owner)
 *   GET  /api/portal/archive/restores                     → ArchiveRestore[] (whole account)
 *   POST /api/portal/archive/restores/:id/cancel          → ArchiveRestore (owner)
 */

import type { Entitlements } from './portal';

// ── Constants ──────────────────────────────────────────────────────────────

export const ARCHIVE_CHUNK_VERSION = 1;
export const ARCHIVE_HOUR_MS = 3_600_000;

export const ARCHIVE_HEADERS = {
  hourStart: 'X-Archive-Hour-Start',
  pointCount: 'X-Archive-Point-Count',
  sha256: 'X-Archive-Sha256',
  part: 'X-Archive-Part',
  parts: 'X-Archive-Parts',
} as const;

/** The appliance uploads an hour only once it ended at least this long ago. */
export const ARCHIVE_UPLOAD_GRACE_MS = 5 * 60_000;
/** On first enable (no watermark, nothing in the cloud) the appliance backfills at most this much history. */
export const ARCHIVE_BACKFILL_DAYS = 7;
/** Above this many points an hour is split into parts, by tag. */
export const ARCHIVE_PART_MAX_POINTS = 400_000;
/** Longest range a raw CSV export may cover. Hourly exports may cover the whole retention. */
export const ARCHIVE_RAW_EXPORT_MAX_DAYS = 31;
/** Most tags one export or restore may name. */
export const ARCHIVE_MAX_TAGS_PER_REQUEST = 200;
/** Longest range one restore job may cover. */
export const ARCHIVE_RESTORE_MAX_DAYS = 366;
/** Data of an account with no archive plan at all is kept this long after the plan lapsed. */
export const ARCHIVE_NO_PLAN_GRACE_DAYS = 30;
/** Standard archive retention; restore needs more than this (Archive Long-Term or Cloud Complete Pro). */
export const ARCHIVE_STANDARD_RETENTION_DAYS = 395;

/** Restore to an appliance is an Archive Long-Term feature. */
export function archiveRestoreAvailable(retentionDays: number): boolean {
  return retentionDays > ARCHIVE_STANDARD_RETENTION_DAYS;
}

// ── Chunk ──────────────────────────────────────────────────────────────────

export type ArchivePointValue = number | boolean | string;
export type ArchivePoint = [msOffset: number, value: ArchivePointValue] | [msOffset: number, value: ArchivePointValue, text: string | null];

export interface ArchiveChunkTag {
  deviceId: string;
  tagPath: string;
  /** Display name at upload time; the tag path when it has none. */
  name?: string;
  dataType?: string;
  points: ArchivePoint[];
}

export interface ArchiveChunk {
  v: typeof ARCHIVE_CHUNK_VERSION;
  hardwareId: string;
  /** ISO 8601 UTC, a whole hour: `2026-09-14T10:00:00.000Z`. */
  hourStart: string;
  /** 0-based; default 0. */
  part?: number;
  /** Default 1. */
  parts?: number;
  /** deviceId → device name at upload time. */
  devices: Record<string, string>;
  tags: Record<string, ArchiveChunkTag>;
}

// ── Helpers shared by both ends ────────────────────────────────────────────

/** Canonical ISO form of an hour start, or null when `ms` is not a whole UTC hour. */
export function archiveHourIso(ms: number): string | null {
  if (!Number.isFinite(ms) || ms % ARCHIVE_HOUR_MS !== 0) return null;
  return new Date(ms).toISOString();
}

/** Parses an hour start; accepts only the canonical `YYYY-MM-DDTHH:00:00.000Z` (or without millis). */
export function parseArchiveHour(value: string): number | null {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:00:00(\.000)?Z$/.test(value)) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) && ms % ARCHIVE_HOUR_MS === 0 ? ms : null;
}

export function floorToArchiveHour(ms: number): number {
  return Math.floor(ms / ARCHIVE_HOUR_MS) * ARCHIVE_HOUR_MS;
}

export function archiveTagKey(deviceId: string | number, tagPath: string, dataTypeSuffix?: string): string {
  return dataTypeSuffix === undefined ? `${deviceId}:${tagPath}` : `${deviceId}:${tagPath}@${dataTypeSuffix}`;
}

export function archiveObjectKey(hardwareId: string, hourStartMs: number, part: number, parts: number, sha256Hex: string): string {
  const iso = new Date(hourStartMs).toISOString();
  const [yyyy, mm, dd, hh] = [iso.slice(0, 4), iso.slice(5, 7), iso.slice(8, 10), iso.slice(11, 13)];
  const partSuffix = parts > 1 ? `.p${part}of${parts}` : '';
  return `archive/${hardwareId}/${yyyy}/${mm}/${dd}/${hh}${partSuffix}.${sha256Hex.slice(0, 16)}.json.gz`;
}

/** `YYYY-MM` of the UTC calendar month containing `ms` — the allowance period. */
export function archiveMonth(ms: number): string {
  return new Date(ms).toISOString().slice(0, 7);
}

/** Start of the next UTC calendar month, when a spent allowance renews. */
export function nextArchiveMonthStart(ms: number): number {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
}

// ── Errors ─────────────────────────────────────────────────────────────────

/**
 * Archive endpoints add a machine-readable `code` to the error envelope:
 * `{ success: false, error, code }`. The appliance acts on the code, never on
 * the message.
 *
 * - 403 not_registered / archive_off — this appliance is not registered, or
 *   archive is not switched on for it: stop until services change.
 * - 402 not_entitled — the account has no archive plan: stop.
 * - 429 allowance_exhausted — this month's points are spent: pause until the
 *   next UTC month.
 * - 400 hour_outside_retention — the hour is older than the plan keeps: skip it.
 * - 400 hour_in_future, checksum_mismatch, invalid_chunk — retry, then skip.
 * - 413 too_large — body or decompressed size over the cap.
 * - 403 restore_requires_longterm — restore needs Archive Long-Term.
 * - 429 rate_limited — too many requests per minute: back off briefly (NOT the allowance).
 * - 401 unauthorized — the licence key was refused.
 */
export type ArchiveErrorCode =
  | 'not_registered'
  | 'archive_off'
  | 'not_entitled'
  | 'allowance_exhausted'
  | 'hour_outside_retention'
  | 'hour_in_future'
  | 'checksum_mismatch'
  | 'invalid_chunk'
  | 'too_large'
  | 'restore_requires_longterm'
  | 'not_found'
  | 'rate_limited'
  | 'unauthorized'
  | 'invalid_request';

export interface ArchiveErrorBody {
  success: false;
  error: string;
  code: ArchiveErrorCode;
}

// ── Appliance-facing (/api/v1/archive, licence key) ────────────────────────

/** POST /api/v1/archive/chunks → 200 (both for a new chunk and a duplicate). */
export interface ArchiveIngestResponse {
  accepted: boolean;
  /** Same hour, part and sha256 as a chunk already stored: nothing changed or was counted. */
  duplicate: boolean;
  /** Same hour and part with different bytes: the new chunk replaced the old. */
  replaced: boolean;
  pointCount: number;
  /** Points accepted from this appliance in the current UTC month, this chunk included. */
  monthPointsUsed: number;
  monthPointsAllowed: number;
}

/** GET /api/v1/archive/state — where the appliance should resume. */
export interface ArchiveStateResponse {
  /** True only when registered, entitled and switched on for THIS appliance. */
  enabled: boolean;
  /** Why not, when enabled is false. */
  reason: ArchiveErrorCode | null;
  /** Start of the newest hour stored for this appliance, or null. */
  latestHour: string | null;
  /** Oldest hour the cloud will accept (retention start), or null when not entitled. */
  earliestAllowedHour: string | null;
  retentionDays: number;
  /** `YYYY-MM`, UTC. */
  month: string;
  monthPointsUsed: number;
  monthPointsAllowed: number;
  monthPointsRemaining: number;
  /** Largest gzip body accepted, bytes. */
  maxChunkBytes: number;
  restoreAvailable: boolean;
}

/** One hour a restore must fetch: GET /api/v1/archive/chunks/:hourStart?part=N for N in [0, parts). */
export interface ArchiveRestoreHour {
  hourStart: string;
  parts: number;
}

/** GET /api/v1/archive/restores/next → `{ job: ArchiveRestoreJob | null }`. */
export interface ArchiveRestoreJob {
  id: string;
  /** ISO, inclusive. */
  from: string;
  /** ISO, exclusive. */
  to: string;
  /** null = every tag. */
  tagKeys: string[] | null;
  /** Hours with data overlapping [from, to), oldest first. */
  hours: ArchiveRestoreHour[];
  hoursTotal: number;
  /** Already reported done; a resumed job may skip this many leading hours. */
  hoursDone: number;
  status: 'queued' | 'in_progress';
}

export interface ArchiveRestoreNextResponse {
  job: ArchiveRestoreJob | null;
}

/** POST /api/v1/archive/restores/:id/progress */
export interface ArchiveRestoreProgressRequest {
  hoursDone: number;
  hoursTotal: number;
  status: 'in_progress' | 'done' | 'failed';
  pointsWritten?: number;
  error?: string;
}

/** The job's status after the update — `canceled` tells the appliance to stop. */
export interface ArchiveRestoreProgressResponse {
  status: ArchiveRestoreStatus;
}

// ── Portal (/api/portal/archive, customer session) ─────────────────────────

export interface ArchiveMonthUsage {
  /** `YYYY-MM`, UTC. */
  month: string;
  pointsUsed: number;
  pointsAllowed: number;
}

export interface ArchiveApplianceSummary {
  /** Portal appliance id. */
  applianceId: string;
  hardwareId: string;
  name: string;
  site: string;
  /** The portal switch. */
  archiveEnabled: boolean;
  /** Switch on AND the account is entitled: the appliance should be uploading. */
  active: boolean;
  /** Distinct hours stored. */
  hoursArchived: number;
  chunkCount: number;
  firstHour: string | null;
  lastHour: string | null;
  pointCount: number;
  bytes: number;
  tagCount: number;
  month: ArchiveMonthUsage;
  lastUploadAt: string | null;
  restoresActive: number;
}

/** GET /api/portal/archive */
export interface ArchiveOverview {
  entitlement: Entitlements['archive'];
  restoreAvailable: boolean;
  rawExportMaxDays: number;
  appliances: ArchiveApplianceSummary[];
}

export interface ArchiveTag {
  tagKey: string;
  deviceId: string;
  tagPath: string;
  deviceName: string;
  tagName: string;
  dataType: string | null;
  /** Has numeric points, so hourly rollups (and hourly export) exist. */
  numeric: boolean;
  firstHour: string;
  lastHour: string;
}

/** GET /api/portal/archive/appliances/:id/tags?search=&limit=&offset= */
export interface ArchiveTagList {
  total: number;
  tags: ArchiveTag[];
}

export type ArchiveExportResolution = 'raw' | 'hour';

/**
 * GET /api/portal/archive/appliances/:id/export?tag=K1&tag=K2&from=&to=&resolution=raw|hour
 * or POST the same fields as JSON. Answers text/csv as an attachment.
 *
 * raw:  timestamp,device,tag,value            (raw range ≤ ARCHIVE_RAW_EXPORT_MAX_DAYS)
 * hour: timestamp,device,tag,count,min,max,avg,last   (numeric tags only)
 *
 * Timestamps are ISO 8601 UTC. Text cells beginning with = + - @ tab or CR are
 * prefixed with an apostrophe so spreadsheets do not run them as formulas.
 */
export interface ArchiveExportRequest {
  tagKeys: string[];
  /** ISO, inclusive. */
  from: string;
  /** ISO, exclusive. */
  to: string;
  resolution: ArchiveExportResolution;
}

export type ArchiveRestoreStatus = 'queued' | 'in_progress' | 'done' | 'failed' | 'canceled';

export interface ArchiveRestore {
  id: string;
  applianceId: string | null;
  hardwareId: string;
  applianceName: string;
  from: string;
  to: string;
  tagKeys: string[] | null;
  status: ArchiveRestoreStatus;
  hoursTotal: number;
  hoursDone: number;
  pointsWritten: number;
  error: string | null;
  requestedBy: string;
  createdAt: string;
  startedAt: string | null;
  updatedAt: string;
  finishedAt: string | null;
}

/** POST /api/portal/archive/appliances/:id/restores (account owner; Archive Long-Term). */
export interface CreateArchiveRestoreRequest {
  from: string;
  to: string;
  /** Omit or null for every tag. */
  tagKeys?: string[] | null;
}
